import type { CustomFieldType } from "./custom-field";
import type { TeamMemberGroup, TeamMemberGroupType } from "./team-member-group";

export const RateDifferentialTypeValues = ["pct_diff", "dollar_diff"] as const;
export type RateDifferentialType = (typeof RateDifferentialTypeValues)[number];

export const RateDifferentialTriggerTypeValues = [
  "prg",
  "shift",
  "job",
  "team_member",
  "custom_field",
] as const;
export type RateDifferentialTriggerType = (typeof RateDifferentialTriggerTypeValues)[number];

export const RateDifferentialPRGOperatorValues = ["=", "in"] as const;
export type RateDifferentialPRGOperator = (typeof RateDifferentialPRGOperatorValues)[number];

export type RateDifferentialPRGTrigger = {
  type: "prg";
  /**
   * How the condition is matched against a timesheet ("=" for a single PRG, "in" for a set).
   * Optional in M1 until the backfill migration ships in 1.3; missing values should be treated as "=".
   */
  operator?: RateDifferentialPRGOperator;
  condition: {
    prg_id: string;
    classification_ids?: string[];
  };
};

export const RateDifferentialShiftOperatorValues = [
  "mostly_between",
  "starts_between",
  "only_between",
] as const;
export type RateDifferentialShiftOperator = (typeof RateDifferentialShiftOperatorValues)[number];

export type RateDifferentialShiftTrigger = {
  type: "shift";
  operator: RateDifferentialShiftOperator;
  condition: {
    start_time: string;
    end_time: string;
    days_of_week: number[];
  };
};

export const RateDifferentialJobOperatorValues = ["in"] as const;
export type RateDifferentialJobOperator = (typeof RateDifferentialJobOperatorValues)[number];

export type RateDifferentialJobTrigger = {
  type: "job";
  operator: RateDifferentialJobOperator;
  condition: {
    job_ids: string[];
  };
};

export const RateDifferentialTeamMemberOperatorValues = ["in", "not_in"] as const;
export type RateDifferentialTeamMemberOperator = (typeof RateDifferentialTeamMemberOperatorValues)[number];

/** Group types a team member trigger can use: each resolves from fields on the team member, since pay rate determination is synchronous. */
export const RateDifferentialTeamMemberGroupTypeValues = [
  "all_team_members",
  "team_member",
  "pay_type",
  "department",
  "location",
  "class",
  "employment_type",
  "title",
  "pay_schedule",
  "company_entity",
] as const satisfies readonly TeamMemberGroupType[];

export type RateDifferentialTeamMemberTrigger = {
  type: "team_member";
  operator: RateDifferentialTeamMemberOperator;
  condition: {
    groups: TeamMemberGroup[];
  };
};

export const RateDifferentialCustomFieldOperatorValues = ["in"] as const;
export type RateDifferentialCustomFieldOperator = (typeof RateDifferentialCustomFieldOperatorValues)[number];

export const RateDifferentialCustomFieldTypeValues = [
  "select",
  "checkbox",
] as const satisfies readonly CustomFieldType[];

export type RateDifferentialCustomFieldTrigger = {
  type: "custom_field";
  operator: RateDifferentialCustomFieldOperator;
  condition: {
    custom_field_id: string;
    /** Option values for select fields; `["true"]` for checkbox fields. */
    values: string[];
  };
};

export type RateDifferentialTrigger =
  | RateDifferentialPRGTrigger
  | RateDifferentialShiftTrigger
  | RateDifferentialJobTrigger
  | RateDifferentialTeamMemberTrigger
  | RateDifferentialCustomFieldTrigger;

export type RateDifferentialTriggerByType = { [T in RateDifferentialTrigger as T["type"]]: T };

export const RateDifferentialMatchModeValues = ["and", "or"] as const;
export type RateDifferentialMatchMode = (typeof RateDifferentialMatchModeValues)[number];
